// These functions run a callback if or when the user allows a category
// Example:
//
//  const termly = await import('../util/termly')
//  termly.allowPerformance(() => {
//    console.log('performance allowed')
//  })

import { setIfChanged } from "./cookies";

export const CATEGORIES = Object.freeze({
  ADVERTISING: "advertising",
  ANALYTICS: "analytics",
  ESSENTIAL: "essential",
  PERFORMANCE: "performance",
  SOCIAL_NETWORKING: "social_networking",
  UNCLASSIFIED: "unclassified",
});

export const ADVERTISING_COOKIES = Object.freeze([
  "_rc_uuid",
  "_obcid",
  "_tbcid",
  "_dicid",
  "_sacid",
  "_sncid",
  "_gclid",
  "_gbraid",
  "_wbraid",
  "_msclkid",
  "_fbclid",
  "_fbp",
  "_fbc",
]);

/**
 * Run `callback` once consent covers the given cookie. A preset list maps some
 * cookies to categories in case Termly's own cookie list fails to load, so we
 * always handle them according to the user's preferences.
 *
 * @param {string} cookie - Name of the cookie to check.
 * @param {function} callback - Runs once consent covers the cookie.
 */
export const allowCookie = (cookie, callback) => {
  ready(() => {
    // Analytics cookies. (mtn-subscriber-status and the modal seen-flags
    // left this map when MTN-1444 reclassified them as functional/Essential
    // and routed them through the storage adapter.)
    if (
      [
        "mtn-ga4-client-id",
        "mtn-ga4-session-id",
        "mtn-user-id",
        "mtn-recommender-user-id",
      ].includes(cookie)
    ) {
      allowAnalytics(callback);
    } else if (ADVERTISING_COOKIES.includes(cookie)) {
      // Advertising Cookies
      allowAdvertising(callback);
    } else if (isAllowedCookie(cookie)) {
      // console.log(`${cookie} is allowed`)
      callback();
    } else {
      document.addEventListener(
        "termlyDataUpdate",
        () => allowCookie(cookie, callback),
        { once: true }
      );
    }
  });
};

export const isAllowedCookie = (cookie) => {
  // Check the cookie whitelist
  const cookies = window.termlyData?.cookies || [];
  if (cookies.some((e) => e.name === cookie)) {
    return true;
  }

  // Check window.termlyCookies, which holds every cookie Termly knows about,
  // and see whether the user allowed the given cookie's category
  if (window.termlyCookies) {
    const found = Object.values(window.termlyCookies).flatMap((array) =>
      array.filter((item) => item.name === cookie)
    );
    // The cookie counts as allowed only if the user consented to its category
    if (found.length > 0 && hasCategory(found[0].category)) {
      return true;
    }
  }

  return false;
};

// Run callback once `condition` holds. If it doesn't yet, wait for the next
// termlyDataUpdate and re-check. {once: true} keeps the listener set
// bounded — the recursive call re-registers a fresh listener only while the
// condition still fails.
const onCondition = (condition, callback) => {
  ready(() => {
    if (condition()) {
      callback();
    } else {
      document.addEventListener(
        "termlyDataUpdate",
        () => onCondition(condition, callback),
        { once: true }
      );
    }
  });
};

const onAllowed = (category, callback) =>
  onCondition(() => hasCategory(category), callback);

/**
 * Deny-side view of Termly's sale/share opt-out flag. A truthy drifted value
 * remains restrictive, but only allowsDataSale() can authorize a sale gate
 * (MTN-1450, MTN-1439).
 *
 * @returns {boolean} - True when the visitor opted out of sale/share.
 */
export const doNotSell = () =>
  Boolean(window.termlyData?.consentState?.do_not_sell);

/**
 * True once Termly resolves without a sale/share opt-out. Termly applies GPC
 * itself, so an absent do_not_sell flag in a resolved category report means
 * no opt-out. Unresolved state still cannot satisfy a sale gate.
 *
 * @returns {boolean} - True when Termly resolved without an opt-out.
 */
export const allowsDataSale = () =>
  Array.isArray(window.termlyData?.categories) && !doNotSell();

/**
 * Run `callback` once Advertising is allowed AND do_not_sell is off. Vendors
 * that receive identifiers or that sell/share data (SmartAsset, Quantcast,
 * Meta advanced matching) require both signals, not just the Advertising
 * category (MTN-1442).
 *
 * @param {function} callback - Runs once both conditions hold.
 */
export const allowAdvertisingSale = (callback) =>
  onCondition(
    () => hasCategory(CATEGORIES.ADVERTISING) && allowsDataSale(),
    callback
  );

export const allowPerformance = (callback) =>
  onAllowed(CATEGORIES.PERFORMANCE, callback);
export const allowAnalytics = (callback) =>
  onAllowed(CATEGORIES.ANALYTICS, callback);
export const allowAdvertising = (callback) =>
  onAllowed(CATEGORIES.ADVERTISING, callback);
export const allowSocialNetworking = (callback) =>
  onAllowed(CATEGORIES.SOCIAL_NETWORKING, callback);
export const allowUnclassified = (callback) =>
  onAllowed(CATEGORIES.UNCLASSIFIED, callback);

/**
 * Sets the Termly UUID cookie from `window.termlyData.uuid`, waiting for Termly to supply a UUID if the termlyData object doesn't carry one yet.
 *
 * @function
 * @name setTermlyUUIDCookie
 *
 * @returns {undefined}
 *
 * @description
 * When `window.termlyData` has a uuid property, sets the Termly UUID cookie to that uuid.
 * Otherwise waits for the next 'termlyDataUpdate' event and calls setTermlyUUIDCookie again.
 * Cookie details:
 *   - Cookie name: `mtn-termly-uuid`
 *   - Domain: .moneytalksnews.com (consent-enforcement bridge covers every subdomain)
 *   - Max age: 90 days (MTN-1434 disposition), refreshed only when Termly
 *     issues a new UUID or on the first load after the cookie expires —
 *     never by a page load alone (MTN-321)
 *   - Path: /
 *   - Secure: true
 *   - SameSite: Lax
 */
export const setTermlyUUIDCookie = () => {
  if (window.termlyData?.uuid) {
    setIfChanged("mtn-termly-uuid", window.termlyData.uuid, {
      days: 90,
      parent: true,
    });
  } else {
    document.addEventListener("termlyDataUpdate", setTermlyUUIDCookie, {
      once: true,
    });
  }
};

/**
 * Runs `callback` once the `termlyData` object is ready. A null value means
 * Termly hasn't reported yet, so it waits for the first `termlyDataUpdate`.
 *
 * @param {function} callback - Runs once `termlyData` is ready.
 */
export const ready = (callback) => {
  if (window.termlyData === null) {
    document.addEventListener("termlyDataUpdate", callback, { once: true });
  } else {
    callback();
  }
};

/**
 * Checks if a given category exists in the termlyData.categories array.
 *
 * @param {string} category - The category to check.
 * @returns {boolean} - True if the category exists, false otherwise.
 */
export const hasCategory = (category) => {
  // Array check fails closed on a malformed report: a string value would
  // otherwise substring-match category names via String.includes.
  const categories = window.termlyData?.categories;
  return Array.isArray(categories) && categories.includes(category);
};

/**
 * Report the current consent state for a category and every later change.
 * A null termlyData value means Termly hasn't resolved yet, so it waits for
 * Termly's first report.
 *
 * @param {string} category - The Termly category to observe.
 * @param {function} callback - Receives true when allowed and false when denied.
 * @returns {function} Stop observing the category.
 */
export const watchCategory = (category, callback) => {
  let hasReported = false;
  let lastState;

  const report = () => {
    if (window.termlyData === null) {
      return;
    }

    const state = hasCategory(category);
    if (hasReported && state === lastState) {
      return;
    }

    hasReported = true;
    lastState = state;
    callback(state);
  };

  document.addEventListener("termlyDataUpdate", report);
  report();

  return () => document.removeEventListener("termlyDataUpdate", report);
};

/**
 * Report when Termly fails to load or does not resolve before a deadline.
 * Resolving termlyData first cancels both unavailable paths.
 *
 * @param {function} callback - Receives the reason Termly is unavailable.
 * @param {{timeoutMs: number}} options - Deadline before reporting a timeout.
 * @returns {function} Stop waiting for an unavailable result.
 */
export const onUnavailable = (callback, { timeoutMs }) => {
  if (window.termlyData !== null) {
    return () => undefined;
  }

  let settled = false;
  let timeoutId;

  const cleanup = () => {
    clearTimeout(timeoutId);
    document.removeEventListener("termlyDataUpdate", handleDataUpdate);
    document.removeEventListener("termlyLoadFailed", handleLoadFailed);
  };
  const settle = (reason) => {
    if (settled) {
      return;
    }
    settled = true;
    cleanup();
    callback(reason);
  };
  const handleDataUpdate = () => {
    if (window.termlyData !== null) {
      settled = true;
      cleanup();
    }
  };
  const handleLoadFailed = () => settle("termly_load_failed");

  document.addEventListener("termlyDataUpdate", handleDataUpdate);
  document.addEventListener("termlyLoadFailed", handleLoadFailed);
  timeoutId = setTimeout(() => settle("fallback_timer"), timeoutMs);

  return () => {
    settled = true;
    cleanup();
  };
};
